// src/pages/WaterQualityTrends.tsx
import {
  Typography as MuiTypography,
  Divider as MuiDivider,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import useQueryParams from "../../hooks/useQueryParams";
import FiltersForm from "../../components/FiltersForm";
import SeasonalMeanChartContainer from "../../components/charts/seasonalMeanChart/SeasonalMeanChartContainer";
import DrilldownScatterChartContainer from "../../components/charts/drilldownScatterChart/DrilldownScatterChartContainer";
import RawTable from "../../components/RawTable";
import { getWaterYearRange } from "../fieldDepthProfile/FieldDepthProfile";
import React from "react";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const { start_date, end_date } = getWaterYearRange();

const PARAMETER_OPTIONS = [
  { label: "Chlorophyll-a", value: "24" },
  { label: "Total Nitrogen", value: "29" },
  { label: "Total Phosphorus", value: "28" },
];

const LOCATION_OPTIONS = [
  { label: "CCR1 – Cherry Creek Res. Station 1", value: "79" },
  { label: "CCR2 – Cherry Creek Res. Station 2", value: "80" },
  { label: "CCR3 – Cherry Creek Res. Station 3", value: "81" },
];

const SEASON_OPTIONS = [
  { label: "Annual", value: "annual" },
  { label: "Summer", value: "summer" },
];

const DEFAULTS = {
  parameterid: 24,
  start_date,
  end_date,
  season: "annual",
  locationIds: LOCATION_OPTIONS.map((loc) => loc.value),
};

const schema = z.object({
  parameterid: z.coerce.number().default(DEFAULTS.parameterid),
  start_date: z.string().default(DEFAULTS.start_date),
  end_date: z.string().default(DEFAULTS.end_date),
  season: z
    .enum(SEASON_OPTIONS.map((season) => season.value))
    .default(DEFAULTS.season),

  // ✨  always string[]
  locationIds: z
    .preprocess(
      (v) => {
        if (Array.isArray(v)) return v; // ["79","80"]
        if (typeof v === "string") return v.split(","); // "79,80" → ["79","80"]
        return [];
      },
      z.array(z.string()) // <- keep as string
    )
    .default(DEFAULTS.locationIds),
});

export default function WaterQualityTrends() {
  const { params, setParams } = useQueryParams(schema);

  const COLUMNS_SEASONAL_MEAN = [
    { title: "Date", field: "date_time", type: "date" },
    { title: "Location", field: "location" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    { title: "Standard", field: "chlora_standard" },
  ];

  const COLUMNS_DRILLDOWN = [
    { title: "Date", field: "rdate", type: "date" },
    { title: "Time", field: "rtime", type: "time" },
    { title: "Location", field: "location_name" },
    { title: "Parameter", field: "parameter" },
    { title: "Value", field: "rvalue_converted", type: "numeric" },
    { title: "Unit", field: "unit_name" },
    { title: "Depth", field: "depth_value", type: "numeric" },
  ];

  const PARAM_FILTERS = [
    {
      type: "select",
      param: "parameterid",
      label: "Parameter",
      default: String(params.parameterid),
      options: PARAMETER_OPTIONS,
    },
  ];

  const DATE_FILTERS = [
    {
      type: "date",
      param: "start_date",
      label: "Start Date",
      default: params.start_date,
    },
    {
      type: "date",
      param: "end_date",
      label: "End Date",
      default: params.end_date,
    },
    {
      type: "select",
      param: "season",
      label: "Season",
      default: params.season,
      options: SEASON_OPTIONS,
    },
  ];

  return (
    <>
      <Helmet title="Reservoir Water Quality Summary" />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          Reservoir Water Quality Summary
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            This page shows long-term trends in surface-water quality for key
            parameters such as <strong>Chlorophyll-a</strong>,{" "}
            <strong>Total Nitrogen</strong>, and{" "}
            <strong>Total Phosphorus</strong> across multiple reservoir
            stations.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Seasonal means are compared to{" "}
            <strong>water quality standards</strong> to assess compliance and
            highlight years where targets may have been exceeded. A separate
            drill-down view provides detailed sampling results by site and date.
          </Typography>
          <Typography variant="body1" gutterBottom>
            Use this tool to evaluate changes in seasonal concentrations over
            time and identify patterns across years, seasons, and locations.
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Tip:</strong> As you adjust the filters, the chart and table
            update automatically. The page URL updates too, so you can{" "}
            <strong>bookmark</strong> or
            <strong> share a direct link</strong> to your current view.
          </Typography>
        </Section>

        <Section style={{ border: "3px solid #c0d0f9" }}>
          <Typography variant="h5" component="h2">
            Parameter Filter
          </Typography>
          <Typography
            variant="body2"
            style={{ marginBottom: 12, fontStyle: "italic" }}
          >
            Selected parameter updates <strong>both</strong> graphs below.
          </Typography>
          <FiltersForm config={PARAM_FILTERS} onChange={setParams} />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Seasonal Mean Concentrations
          </Typography>
          <SeasonalMeanChartContainer
            parameterid={params.parameterid}
            endYear={new Date().getFullYear()}
          />

          <Section style={{ margin: "16px 0 0 0 " }}>
            <Typography variant="h5" component="h3" gutterBottom>
              About this Graph
            </Typography>
            <Typography variant="body1" gutterBottom>
              This scatterplot shows{" "}
              <strong>seasonal mean concentrations</strong> for the selected
              parameter, aggregated across all available years in the chosen
              date range.
            </Typography>

            <Typography variant="body1" gutterBottom>
              Each point represents the <strong>average seasonal value</strong>{" "}
              for a specific year. A <strong>horizontal red line</strong>{" "}
              indicates the applicable water quality standard (if available) for
              that season and parameter.
            </Typography>

            <Typography variant="body1" gutterBottom>
              Hover over a point to view the exact year, value, and standard.
              The tooltip background color indicates whether the{" "}
              <strong>seasonal mean concentration</strong> for a given year met
              the standard. A <strong>green tooltip</strong> means the value was{" "}
              <em>below</em> the standard, while a <strong>red tooltip</strong>{" "}
              means it was <em>above</em>.
            </Typography>
          </Section>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Drill-down by Location
          </Typography>
          <Section style={{ marginTop: "16px" }}>
            <Typography variant="h6" component="h3" gutterBottom>
              Date Range & Season Filters
            </Typography>
            <FiltersForm config={DATE_FILTERS} onChange={setParams} />
          </Section>
          <DrilldownScatterChartContainer
            parameterid={params.parameterid}
            startDate={params.start_date}
            endDate={params.end_date}
            season={params.season}
            locationIds={params.locationIds}
          />

          <Section style={{ margin: "16px 0 0 0 " }}>
            <Typography variant="h5" component="h3" gutterBottom>
              About this Graph
            </Typography>
            <Typography variant="body1" gutterBottom>
              This scatterplot shows{" "}
              <strong>individual sampling results</strong> for the selected
              parameter across the chosen locations and time range.
            </Typography>

            <Typography variant="body1" gutterBottom>
              Each point represents a <strong>single measurement</strong>.
              Colors distinguish between locations, and a vertical blue line
              follows your mouse to help align events across time.
            </Typography>

            <Typography variant="body1" gutterBottom>
              Use this graph to explore{" "}
              <strong>variability and seasonality</strong> in the raw data, or
              to identify <strong>high or outlier values</strong> at specific
              locations.
            </Typography>
          </Section>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Seasonal Mean Concentrations Raw Data Table
          </Typography>

          <RawTable
            endPoint="avg-yearly-standard-graph"
            params={{
              parameterid: params.parameterid,
              end_year: new Date().getFullYear(),
            }}
            columns={COLUMNS_SEASONAL_MEAN}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Drill-down by Location Raw Data Table
          </Typography>

          <RawTable
            endPoint="results-simple/wq-parameter-viewer/drilldown"
            params={{
              ...params,
              ...{ locationIds: params.locationIds.join(",") },
            }}
            columns={COLUMNS_DRILLDOWN}
          />
        </Section>
      </main>
    </>
  );
}
